/**
 * maintenanceStore.ts
 *
 * Holds the maintenance window read from remote configuration and decides,
 * against the current clock, whether the app should be showing the
 * maintenance page.
 *
 * The store deliberately knows nothing about who is allowed to bypass
 * maintenance - that belongs to the router guard, which is the only place
 * with both this state and the signed-in user's role.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRemoteConfig } from '@/services/remoteConfig'
import {
  INACTIVE_MAINTENANCE_WINDOW,
  type MaintenanceWindow,
  isMaintenanceActive,
} from '@/utils/maintenance'

/**
 * How often the app re-reads the maintenance flag, in milliseconds.
 *
 * Five minutes is the shortest interval that stays clear of Firebase Remote
 * Config's fetch throttling while still bounding how long a user can keep
 * working after an operator has switched maintenance on.
 */
export const MAINTENANCE_POLL_INTERVAL_MS = 5 * 60 * 1000

export const useMaintenanceStore = defineStore('maintenance', () => {
  const remoteConfig = useRemoteConfig()

  const window = ref<MaintenanceWindow>({ ...INACTIVE_MAINTENANCE_WINDOW })
  const isChecking = ref(false)
  /**
   * The instant the window is evaluated against.
   *
   * Held as state rather than read inline so that `isMaintenanceMode` stays
   * a computed: a scheduled window must start and end on its own as the
   * clock advances, which it can only do if the clock is reactive.
   */
  const evaluatedAt = ref(Date.now())

  let pollTimer: ReturnType<typeof setInterval> | null = null

  const isMaintenanceMode = computed(() =>
    isMaintenanceActive(window.value, evaluatedAt.value),
  )
  const from = computed(() => window.value.from)
  const to = computed(() => window.value.to)

  /**
   * Re-reads the maintenance window and re-evaluates it against the clock.
   *
   * A failed fetch leaves the last known window in place: losing the
   * network says nothing about whether maintenance is running, and letting
   * the page flip back to normal on a dropped poll would be worse than
   * showing a slightly stale answer.
   *
   * @param force - Bypasses the remote configuration cache.
   */
  async function refresh(force = false): Promise<void> {
    isChecking.value = true
    try {
      window.value = await remoteConfig.fetchMaintenance(force)
    } catch (error) {
      console.error('Failed to read maintenance configuration:', error)
    } finally {
      evaluatedAt.value = Date.now()
      isChecking.value = false
    }
  }

  /**
   * Starts the background poll, if it is not already running.
   *
   * Calling this more than once is harmless; the app starts it once at
   * boot, but a component remounting must not double the request rate.
   */
  function startPolling(): void {
    if (pollTimer !== null) {
      return
    }
    pollTimer = setInterval(() => {
      void refresh()
    }, MAINTENANCE_POLL_INTERVAL_MS)
  }

  /** Stops the background poll. */
  function stopPolling(): void {
    if (pollTimer === null) {
      return
    }
    clearInterval(pollTimer)
    pollTimer = null
  }

  return {
    isMaintenanceMode,
    isChecking,
    from,
    to,
    refresh,
    startPolling,
    stopPolling,
  }
})
